import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';

import { hook } from '@g4/automation-hooks';
import oldTheme from '../Colors';

const IconWrapper = styled.div`
  margin-right: ${props => props.theme.spacing(2)};
  flex-shrink: 0;
`;

const TextWrapper = styled.div`
  flex-grow: 1;

  ${props =>
    props.vAlignText === 'center' &&
    css`
      display: flex;
      align-items: center;
    `};
`;

const FullWidthContainer = styled.div`
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  padding: ${props => props.theme.spacing(props.size)};
  background-color: #fff;
  box-shadow: inset 0 1px 0 0 ${oldTheme.colors.common.border},
    -1px 0 0 0 ${oldTheme.colors.common.border},
    1px 0 0 0 ${oldTheme.colors.common.border},
    0 1px 0 0 ${oldTheme.colors.common.border};

  &:last-child: {
    margin-bottom: ${props => props.theme.spacing(2)};
  }

  ${props => {
    if (props.color) {
      switch (props.color) {
        case 'blue':
          return css`
            background-color: ${oldTheme.colors.modern.strip.blue};
          `;
        case 'yellow':
          return css`
            background-color: ${oldTheme.colors.modern.strip.yellow};
          `;
        case 'green':
          return css`
            background-color: ${oldTheme.colors.modern.strip.green};
          `;
        case 'grey':
          return css`
            background-color: ${oldTheme.colors.modern.strip.grey};
          `;
        default:
          return css`
            background-color: ${props.color};
          `;
      }
    }
  }};
  ${props =>
    props.border &&
    css`
      box-shadow: inset 0 2px 0 0 ${props => props.border};
    `};
`;

export class Strip extends PureComponent {
  static propTypes = {
    /** Associates a specific action onto an element for the benefit of automated clients consuming it */
    automationHook: PropTypes.string,
    /** CSS styles */
    className: PropTypes.string,
    /** All component children nodes */
    children: PropTypes.node,
    /** Color of the strip */
    color: PropTypes.oneOfType([
      PropTypes.oneOf(['blue', 'yellow', 'green', 'grey']),
      // Expect colour value from theme
      PropTypes.string,
    ]),
    vAlignText: PropTypes.oneOf(['top', 'center']),
    /** Icon asset */
    icon: PropTypes.node,
    /** Strip padding multiplied with the gutter size */
    size: PropTypes.number,
    /** Border color to be applied */
    border: PropTypes.string,
    /**
      Alternative text to be rendered when the icon cannot
      be rendered, due to: a slow connection, a bad `src` attribute, or if the
      user uses a screen reader
    */
    iconAltAttribute: PropTypes.string,
    /**
    Handle a click on the component's icon
    */
    onClickIcon: PropTypes.func,
  };

  static defaultProps = {
    vAlignText: 'center',
    size: 2,
  };

  renderIconTag() {
    const { icon, iconAltAttribute } = this.props;

    return <img src={icon} alt={iconAltAttribute} />;
  }

  renderIconWithWrapper() {
    const { onClickIcon } = this.props;

    return (
      <IconWrapper>
        {onClickIcon ? (
          <button onClick={onClickIcon}>{this.renderIconTag()}</button>
        ) : (
          this.renderIconTag()
        )}
      </IconWrapper>
    );
  }

  render() {
    const {
      icon,
      children,
      className,
      size,
      border,
      color,
      automationHook,
    } = this.props;

    return (
      <Container
        {...hook(automationHook)}
        className={className}
        size={size}
        border={border}
        color={color}
      >
        {icon && this.renderIconWithWrapper()}

        <TextWrapper vAlignText={this.props.vAlignText}>
          <FullWidthContainer>{children}</FullWidthContainer>
        </TextWrapper>
      </Container>
    );
  }
}

export default styled(Strip)``;
